import { inject, NgModule, provideAppInitializer } from '@angular/core';
import {
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { authInterceptor } from '../../auth/interceptors/auth.interceptor';
import { BrowserModule } from '@angular/platform-browser';
import { AuthModule as AuthModuleMobile } from '@auth0/auth0-angular';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { metaReducers, reducers } from 'src/app/reducers';
import { environment } from 'src/environments/environment';
import { MobileAppComponent } from './mobile-app.component';
import { Capacitor } from '@capacitor/core';
import { MOBILE_APP_IDS } from './auth/mobile-app-ids';
import { MobileAppRoutingModule } from './mobile-app-routing.module';
import { MobileAuthModule } from './auth/mobile-auth.module';
import {
  provideIonicAngular,
  IonicRouteStrategy,
  IonApp,
  IonRouterOutlet,
  IonContent,
} from '@ionic/angular/standalone';
import { A11yModule } from '@angular/cdk/a11y';
import { AuthGuard } from '../../auth/guards/auth.guard';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Loader } from '@googlemaps/js-api-loader';
import { NgxGpAutocompleteService } from '@angular-magic/ngx-gp-autocomplete';
import { RouteReuseStrategy } from '@angular/router';
import { MobileCoreModule } from './core/mobile-core.module';
import {
  customIosTransitionAnimation,
  customMdTransitionAnimation,
} from './animations/page-transitions';
import {
  authReducer,
  featureName as authFeatureName,
} from '../../auth/store/auth.reducer';
import { BrandingBootstrapService } from './branding/branding-bootstrap.service';
import { MobileBrandingEffects } from './branding/store/mobile-branding.effects';
import { MobileOfflineBannerComponent } from './shared/connectivity/mobile-offline-banner.component';

// NOTE: appId is resolved at runtime from Capacitor.getPlatform() so the bundle
// works on both Android and iOS regardless of which capacitor.config-*.ts
// template was active at build time. On the web (mock dev) we fall back to the
// Android scheme — the value is never used because the mock auth service
// intercepts loginWithRedirect, but it must be a valid string for type safety.
const appId =
  Capacitor.getPlatform() === 'ios'
    ? MOBILE_APP_IDS.ios
    : MOBILE_APP_IDS.android;
export const callbackUri = `${appId}://${environment.auth0Domain}/capacitor/${appId}/callback`;

@NgModule({
  declarations: [MobileAppComponent],
  bootstrap: [MobileAppComponent],
  imports: [
    BrowserModule,
    IonApp,
    IonRouterOutlet,
    IonContent,
    MobileOfflineBannerComponent,
    MobileAppRoutingModule,
    BrowserAnimationsModule,
    A11yModule,
    // NOTE: MobileBrandingEffects observes AccountGlobalStore.account via
    // toObservable() (replaces the legacy `AccountLoaded` ngrx action removed
    // in the SignalStore migration) and drives the dynamic brand swap +
    // native icon change for users with registered brandCodeAffiliations.
    // The boot overlay fade-out fallback is also covered by
    // provideAppInitializer(BrandingBootstrapService) below.
    EffectsModule.forRoot([MobileBrandingEffects]),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    // NOTE: Register auth feature in the main bundle so selectors (e.g.
    // AuthGuard's tokenSelector) can access them immediately. esbuild
    // code-splits MobileAuthModule into a lazy chunk because the routing uses
    // loadChildren, so StoreModule.forFeature inside it won't run until the
    // chunk loads. Dictionary was migrated to DictionaryGlobalStore
    // (signalStore providedIn: 'root') so no registration is needed.
    StoreModule.forFeature(authFeatureName, authReducer),
    environment.reduxToolsEnabled
      ? StoreDevtoolsModule.instrument({
          maxAge: environment.reduxMaxAge,
          logOnly: environment.production,
          serialize: true,
          connectInZone: true,
          actionsBlocklist: [
            '[SdSVG / Others] Sd Cache element Added',
            '[SdSVG] Sd Cache New Image Requested',
            '@ngrx/store/update-reducers',
          ],
        })
      : [],
    MobileAuthModule,
    MobileCoreModule,
    AuthModuleMobile.forRoot({
      domain: environment.auth0Domain,
      clientId: environment.auth0ClientID,
      useRefreshTokens: true,
      useRefreshTokensFallback: false,
      authorizationParams: {
        redirect_uri: callbackUri,
        audience: environment.auth0Audience,
        scope: environment.auth0Scope,
      },
      cacheLocation: 'localstorage',
    }),
    StoreRouterConnectingModule.forRoot(),
  ],
  providers: [
    provideHttpClient(
      withInterceptors([authInterceptor]),
      withInterceptorsFromDi()
    ),
    AuthGuard,
    provideIonicAngular({
      navAnimation: (
        baseEl: HTMLElement,
        opts: {
          enteringEl: HTMLElement;
          leavingEl: HTMLElement;
          direction: 'forward' | 'back';
          mode?: string;
        }
      ) => {
        const mode = opts.mode || 'ios';
        return mode === 'ios'
          ? customIosTransitionAnimation(baseEl, opts)
          : customMdTransitionAnimation(baseEl, opts);
      },
    }),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: NgxGpAutocompleteService,
      useFactory: () => {
        const svc = new NgxGpAutocompleteService();
        svc.setOptions({ types: ['address'] });
        return svc;
      },
    },
    {
      provide: Loader,
      useValue: new Loader({
        apiKey: environment.googleMapsKey,
        libraries: ['places'],
      }),
    },
    provideAppInitializer(() => {
      const bootstrap = inject(BrandingBootstrapService);
      return bootstrap.initialize().catch(err => {
        console.warn('[BrandingBootstrap] initialize failed, continuing', err);
      });
    }),
  ],
})
export class MobileAppModule {}
